import { getExistingCart } from '@shared/components/cart/actions';
import {
  CART_ATTRIBUTE_DIAMOND_LINE_KEY,
  CART_ATTRIBUTE_JEWELRY_LINE_KEY,
  CHECKOUT_CART_COOKIE_NAME,
  DIAMOND_PRODUCT_HANDLE,
  IN_STOCK_DIAMOND_PRODUCT_TYPE,
  JewelryTypeMap,
  KIT_PRODUCT_HANDLE,
  ORDER_TYPE_ATTRIBUTE_KEY,
  ORDER_TYPE_SERVICE,
  PREMIUM_PROCESSING_FEE_PRODUCT_HANDLE,
  QUOTE_CART_COOKIE_NAME,
  SAVED_QUOTE_CART_ATTRIBUTE_KEY,
  SERVICE_ORDER_NUMBER_ATTRIBUTE_KEY,
  SPREAD_4_MONTHS_PLAN_ID,
} from '@shared/lib/constants';
import { createCart, getCart, updateCart } from '@shared/lib/shopify';
import { Attribute, CartItem, Money, Order, OrderType } from '@shared/lib/shopify/types';
import { Cart, DiamondCartLine, JewelryCartLine } from '@shared/lib/shopify/types/shopify-native';
import { addMoney, zeroMoney } from './money';

export function getCartOrderType(cart: Cart | undefined): OrderType {
  if (!cart) return ORDER_TYPE_SERVICE;
  return (cart.attributes?.find((attribute) => attribute.key === ORDER_TYPE_ATTRIBUTE_KEY)?.value ||
    ORDER_TYPE_SERVICE) as OrderType;
}

export function getCartContinueOrderNumber(cart: Cart): number | undefined {
  return parseInt(
    cart.attributes?.find((attribute) => attribute.key === SERVICE_ORDER_NUMBER_ATTRIBUTE_KEY)
      ?.value || ''
  );
}

export function isFirstOrderCart(cart: Cart | undefined): boolean {
  if (!cart) return true;
  const orderType = getCartOrderType(cart);
  return orderType === ORDER_TYPE_SERVICE;
}

export function getDiamondLinesInCart(cart: Cart | undefined): CartItem[] {
  if (!cart) return [];
  return cart.lines.filter((line) => line.merchandise.product.handle === DIAMOND_PRODUCT_HANDLE);
}

export function getJewelryLinesInCart(cart: Cart | undefined): CartItem[] {
  if (!cart) return [];
  return cart.lines.filter((line) =>
    Object.values(JewelryTypeMap).includes(line.merchandise.product.productType)
  );
}

export function groupLinesInCart(cart: Cart | undefined): {
  diamonds: DiamondCartLine[];
  inStockDiamonds: CartItem[];
  jewelry: JewelryCartLine[];
  others: CartItem[];
  premiumProcessing: CartItem[];
  kit: CartItem[];
} {
  const diamonds: DiamondCartLine[] = [];
  const inStockDiamonds: CartItem[] = [];
  const jewelry: JewelryCartLine[] = [];
  const others: CartItem[] = [];
  const premiumProcessing: CartItem[] = [];
  const kit: CartItem[] = [];
  if (!cart) return { diamonds, inStockDiamonds, jewelry, others, premiumProcessing, kit };
  cart.lines.forEach((line) => {
    if (line.merchandise.product.handle === DIAMOND_PRODUCT_HANDLE) {
      diamonds.push({ ...line, type: 'bonded' } as DiamondCartLine);
    } else if (line.merchandise.product.handle === KIT_PRODUCT_HANDLE) {
      kit.push(line);
    } else if (line.merchandise.product.handle === PREMIUM_PROCESSING_FEE_PRODUCT_HANDLE) {
      premiumProcessing.push(line);
    } else if (Object.values(JewelryTypeMap).includes(line.merchandise.product.productType)) {
      jewelry.push(line as JewelryCartLine);
    } else if (line.merchandise.product.productType === IN_STOCK_DIAMOND_PRODUCT_TYPE) {
      inStockDiamonds.push({ ...line, type: 'in-stock' } as DiamondCartLine);
    } else {
      others.push(line);
    }
  });
  jewelry.forEach((jewelryLine, index) => {
    const diamondLineId = jewelryLine.attributes.find(
      (attr) => attr.key === CART_ATTRIBUTE_DIAMOND_LINE_KEY
    )?.value;
    if (diamondLineId) {
      const diamondLineIndex = diamonds.findIndex((line) => line.id === diamondLineId);
      if (diamondLineIndex !== -1) {
        jewelry[index] = {
          ...jewelryLine,
          diamondLine: diamonds![diamondLineIndex],
        } as JewelryCartLine;
        diamonds![diamondLineIndex] = {
          ...diamonds![diamondLineIndex],
          jewelryLine,
        } as DiamondCartLine;
      }
      const inStockDiamondLineIndex = inStockDiamonds.findIndex(
        (line) => line.id === diamondLineId
      );
      if (inStockDiamondLineIndex !== -1) {
        jewelry[index] = {
          ...jewelryLine,
          diamondLine: inStockDiamonds![inStockDiamondLineIndex],
        } as JewelryCartLine;
        inStockDiamonds![inStockDiamondLineIndex] = {
          ...inStockDiamonds![inStockDiamondLineIndex],
          jewelryLine,
        } as DiamondCartLine;
      }
      if (diamondLineIndex === -1 && inStockDiamondLineIndex === -1) {
        console.warn('Diamond line not found for jewelry line', diamondLineId);
      }
    }
  });
  return { diamonds, inStockDiamonds, jewelry, others, premiumProcessing, kit };
}

export function hasSpread4MonthsSellingPlan(cart: Cart | undefined): boolean {
  return getJewelryLinesInCart(cart).some(
    (line) => line.sellingPlanAllocation?.sellingPlan.id === SPREAD_4_MONTHS_PLAN_ID
  );
}

export function hasDepositDiscountCode(cart: Cart | undefined): boolean {
  return (cart?.paymentPlanDiscountAllocations.length || 0) > 0;
}

export function groupLinesInCarts(
  cart: Cart | undefined,
  quoteCart: Cart | undefined,
  isFirstTimeOrder: boolean
): Record<string, CartItem[]> {
  if (isFirstTimeOrder) {
    return {
      ...groupLinesInCart(cart),
      ...groupLinesInCart(quoteCart),
    };
  }
  return groupLinesInCart(cart);
}

export function groupJewelryWithDiamonds(
  bondedDiamonds: CartItem[],
  inStockDiamonds: CartItem[],
  jewelryItems: CartItem[],
  cart: Cart
): {
  jewelries: { jewelry: CartItem; diamonds: CartItem[] }[];
  looseBondedDiamonds: CartItem[];
  looseInStockDiamonds: CartItem[];
} {
  if (jewelryItems.length === 0) {
    return {
      jewelries: [],
      looseBondedDiamonds: bondedDiamonds,
      looseInStockDiamonds: inStockDiamonds,
    };
  }
  const { diamondsByJewelryId, looseBondedDiamonds, looseInStockDiamonds } = bondedDiamonds.reduce(
    (acc, diamond) => {
      const jewelryLine = getJewelryLineFromDiamondLine(diamond, cart);
      if (jewelryLine && jewelryItems.some((jewelry) => jewelry.id === jewelryLine.id)) {
        acc.diamondsByJewelryId[jewelryLine.id] = acc.diamondsByJewelryId[jewelryLine.id] || [];
        acc.diamondsByJewelryId[jewelryLine.id]!.push(diamond);
      } else {
        if (diamond.merchandise.product.productType === IN_STOCK_DIAMOND_PRODUCT_TYPE) {
          acc.looseInStockDiamonds.push(diamond);
        } else {
          acc.looseBondedDiamonds.push(diamond);
        }
      }
      return acc;
    },
    { diamondsByJewelryId: {}, looseBondedDiamonds: [], looseInStockDiamonds: [] } as {
      diamondsByJewelryId: Record<string, CartItem[]>;
      looseBondedDiamonds: CartItem[];
      looseInStockDiamonds: CartItem[];
    }
  );

  return {
    jewelries: jewelryItems.map((jewelry) => ({
      jewelry,
      diamonds: diamondsByJewelryId[jewelry.id] || [],
    })),
    looseBondedDiamonds,
    looseInStockDiamonds,
  };
}

export function getNumberOfDiamondsInCart(cart: Cart | undefined): number {
  return getDiamondLinesInCart(cart).reduce((acc, line) => {
    return acc + line.quantity;
  }, 0);
}

function accumulateTotal(lines: CartItem[]): {
  subtotalAmount: Money;
  totalAmount: Money;
} {
  return lines.reduce(
    (acc, line) => {
      return {
        subtotalAmount: addMoney(acc.subtotalAmount, line.cost.subtotalAmount),
        totalAmount: addMoney(acc.totalAmount, line.cost.totalAmount),
      };
    },
    { subtotalAmount: zeroMoney, totalAmount: zeroMoney }
  );
}

export function getDiamondsCostFromCart(cart: Cart | undefined): {
  subtotalAmount: Money;
  totalAmount: Money;
} {
  return accumulateTotal(getDiamondLinesInCart(cart));
}

export function getTotalCostOfJewelryInCart(cart: Cart | undefined): {
  subtotalAmount: Money;
  totalAmount: Money;
} {
  return accumulateTotal(getJewelryLinesInCart(cart));
}

export function getDiamondTypesFromLines(lines: CartItem[]): Record<string, string[]> {
  return lines.reduce(
    (acc, line) => {
      line.merchandise.selectedOptions.forEach((option) => {
        if (!acc[option.name]) {
          acc[option.name] = [];
        }
        if (!acc[option.name]!.includes(option.value)) {
          acc[option.name]!.push(option.value);
        }
      });
      return acc;
    },
    {} as Record<string, string[]>
  );
}

export function getDiamondTypesInCart(cart: Cart | undefined): Record<string, string[]> {
  return getDiamondTypesFromLines(getDiamondLinesInCart(cart));
}

export async function getCarts(): Promise<{
  cart: Cart | undefined;
  quoteCart: Cart | undefined;
  operatingCart: Cart | undefined;
  isFirstTimeOrder: boolean;
}> {
  const cart = (await getExistingCart(CHECKOUT_CART_COOKIE_NAME)) || undefined;
  const quoteCart = (await getExistingCart(QUOTE_CART_COOKIE_NAME)) || undefined;

  const isFirstTimeOrder = isFirstOrderCart(cart);
  const operatingCart = isFirstTimeOrder ? quoteCart : cart;

  return {
    cart,
    quoteCart,
    operatingCart,
    isFirstTimeOrder,
  };
}

export function getJewelryLineFromDiamondLine(
  diamondLine: CartItem,
  cart: Cart
): CartItem | undefined {
  const jewelryLineId = diamondLine?.attributes.find(
    (attr) => attr.key === CART_ATTRIBUTE_JEWELRY_LINE_KEY
  )?.value;
  return jewelryLineId ? cart.lines.find((line) => line.id === jewelryLineId) : undefined;
}

export function isCartEmpty(cart: Cart | undefined): boolean {
  return !cart || cart?.lines.length === 0;
}

export async function cloneCart(quoteCart: Cart) {
  const newCart = await createCart(
    quoteCart.lines.map((line) => ({
      merchandiseId: line.merchandise.id,
      quantity: line.quantity,
      attributes: line.attributes,
    })),
    quoteCart.discountAllocations.map((allocation) => allocation.code).filter(Boolean) as string[],
    quoteCart.attributes
  );
  // Because the line ids are different, we need to update the saved quote cart attribute.
  const updates: { id: string; attributes: Attribute[] }[] = quoteCart.lines.reduce(
    (acc, line, index) => {
      const jewelryLine = getJewelryLineFromDiamondLine(line, quoteCart);
      if (jewelryLine) {
        const jewelryLineIndex = quoteCart.lines.findIndex((line) => line.id === jewelryLine.id);
        if (jewelryLineIndex !== -1) {
          acc.push({
            id: newCart.lines[index]?.id!,
            attributes: [
              ...(newCart.lines[index]?.attributes || []),
              {
                key: CART_ATTRIBUTE_JEWELRY_LINE_KEY,
                value: newCart.lines[jewelryLineIndex]?.id!,
              },
            ],
          });
        }
      }
      return acc;
    },
    [] as { id: string; attributes: Attribute[] }[]
  );
  await updateCart(newCart.id, updates);
  return newCart;
}

export async function buildCartFromOrder(order: Order): Promise<Cart> {
  return await createCart(
    order.lines.map((line) => ({
      merchandiseId: line.variantId,
      quantity: line.quantity,
      // TODO fix attributes: line.
    })),
    [], // TODO fix order.discountApplications.map((application) => application.value).filter(Boolean) as string[],
    order.customAttributes
  );
}

export async function loadOrBuildQuoteCart(serviceOrder: Order): Promise<Cart> {
  const quoteAttribute = serviceOrder.customAttributes?.find(
    (attr) => attr.key === SAVED_QUOTE_CART_ATTRIBUTE_KEY
  )?.value;
  const parsedQuoteCart = quoteAttribute ? JSON.parse(quoteAttribute) : null;
  if (parsedQuoteCart) {
    const quoteCart = await getCart(parsedQuoteCart.id);
    if (quoteCart) {
      return quoteCart;
    } else {
      try {
        return await cloneCart(parsedQuoteCart);
      } catch (e) {
        console.error(JSON.stringify(e));
        return await createCart();
      }
    }
  }
  return await createCart();
}
export function getCartLineLink(cartLine: CartItem): string | undefined {
  if (cartLine.merchandise.product.handle === DIAMOND_PRODUCT_HANDLE) {
    return '/shop/diamonds/bonded';
  } else if (
    [KIT_PRODUCT_HANDLE, PREMIUM_PROCESSING_FEE_PRODUCT_HANDLE].includes(
      cartLine.merchandise.product.handle
    )
  ) {
    return '/shop/service';
  } else if (Object.values(JewelryTypeMap).includes(cartLine.merchandise.product.productType)) {
    return `/shop/jewelry/${cartLine.merchandise.product.handle}`;
  }
  console.warn('Unknown product type', cartLine.merchandise.product.productType);
  return `/shop/jewelry/${cartLine.merchandise.product.handle}`;
}
